import { Component, Prop, Host, h, EventEmitter, Listen, Method, Event, State, Watch, Element, AttachInternals } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import { stopEventAndImmediatePropagation } from '../../utils';
import { OptionValue } from '../option/option';
import { setFormValue, setValidityMissingValue } from '../../utils/formAssociated';

/**
 *  Parses options to an array of strings in use-cases where the component is used as a web-component by a consumer
 *  This is because an array will be converted to a comma-seperated string when used as a web-component
 *  When used as a Stencil component by another anchor component it will however be an array of strings
 *  Is called before the initial render
 */
const parseOptions = (options: string | string[] | OptionValue[]) => (typeof options === 'object' ? options : JSON.parse(options));

@Component({
  tag: 'anchor-select',
  styleUrl: 'select.scss',
  shadow: {
    delegatesFocus: true
  },
  formAssociated: true,
})
export class Select {
  @Element() host: HTMLElement;

  @AttachInternals() internals: ElementInternals;

  formResetCallback() {
    this.internals.setValidity({});
    setFormValue(this.internals, null);
  }

  formStateRestoreCallback(state) {
    setFormValue(this.internals, state);
    setValidityMissingValue(this.internals, state, this.validityMissingValueText)
  }

  /**
   *  Unique identificator for this DOM node. Default generated by uuidv4.
   */
  @Prop() selectId?: string = uuidv4();

  /**
   *  Values to be shown in the DropDown select
   */
  @Prop() options: string | string[] | OptionValue[];

  /**
   *  Values to be shown in the DropDown select
   */
  @Prop() initialValue?: string = '- Select a value -';

  /**
   *  Value indicating whether or not the DropDown select should be expanded
   */
  @Prop({ reflect: true, mutable: true }) expanded?: boolean = false;

  /**
   *  Value indicating whether or not this particular SelectItem should be disabled from selecting
   */
  @Prop({ reflect: true }) disabled?: boolean = false;

  /**
   * Mark the field as required
   */
  @Prop() required: boolean = false;

  /**
   *  Boolean value indicating whether or not this particular SelectItem should display an error message
   */
  @Prop({ reflect: true }) error?: boolean = false;

  /**
   *  @deprecated use selectedIndex instead. This has the same functionality, but a better name. This field will be removed 23.12.23
   */
  @Prop({ reflect: true, mutable: true }) selectedItem?: number;

  /**
   *  The index of the item which should be pre-selected - defaults to the first item in the Array
   */
  @Prop({ reflect: true, mutable: true }) selectedIndex?: number;

  /**
   * Text to be displayed if when value is missing an native form validation is used
   */
  @Prop() validityMissingValueText?: string;

  /**
   *  Event emitted when the dropdown select expands
   */
  @Event() expand?: EventEmitter;

  /**
   *  Event emitted when the dropdown select collapses
   */
  @Event() collapse?: EventEmitter;

  /**
   *  Event emitted when an select-item is selected - containing the value of that select-item
   */
  @Event() selected?: EventEmitter;

  @State() focusedItem: number = -1;

  @State() parsedOptions: Array<string> | Array<OptionValue> = [];

  /**
   * Called before every render()
   */
  componentWillRender() {
    this.watchOptions(this.options);
  }

  /**
   * Called every time the component is connected to the DOM.
   */
  componentWillLoad() {
    if (!this.selectedIndex && this.selectedItem) {
      this.selectedIndex = this.selectedItem;
    }
    this.watchOptions(this.options);
  }

  /**
   *  Handles conversion between strings and arrays of strings after the initial render if the value passed in options changes
   */
  @Watch('options')
  watchOptions(options: string | string[] | OptionValue[]) {
    if (options) this.parsedOptions = parseOptions(options);
  }

  @Method()
  async focusFirst() {
    const isBeforeFirst = this.focusedItem < 0;
    const setFocusOnFirstItem = () => {
      this.focusedItem = 0;
      return true;
    };
    return isBeforeFirst && setFocusOnFirstItem();
  }

  @Method()
  async focusLast() {
    const lastIndex = this.parsedOptions.length - 1;
    const isAfterLast = this.focusedItem > lastIndex;
    const setFocusOnLastItem = () => {
      this.focusedItem = lastIndex;
      return true;
    };
    return isAfterLast && setFocusOnLastItem();
  }

  @Method()
  async expandComponent(e = undefined) {
    stopEventAndImmediatePropagation(e);
    const setTrue = () => {
      this.expanded = true;
      return this.expanded;
    };
    const emitEvent = () => this.expand.emit(e);
    return !this.disabled && setTrue() && emitEvent();
  }

  @Method()
  async collapseComponent(e = undefined) {
    stopEventAndImmediatePropagation(e);
    const setFalse = () => {
      this.expanded = false;
      return !this.expanded;
    };
    const emitEvent = () => this.collapse.emit(e);
    return !this.disabled && setFalse() && emitEvent();
  }

  @Method()
  async handlePressed(e: MouseEvent, index: number) {
    stopEventAndImmediatePropagation(e);
    return !this.disabled && this.setSelect(index);
  }

  @Method()
  async expandAndFocus() {
    const index = Number.isNaN(this.selectedIndex) || !this.selectedIndex ? 0 : this.selectedIndex;
    return this.expandComponent() && this.setFocus(index);
  }

  @Method()
  async selectAndCollapse(index: number) {
    return this.setSelect(index) && this.collapseComponent();
  }

  @Method()
  async handleNext(e) {
    stopEventAndImmediatePropagation(e);
    return this.expanded ? this.setFocus(this.focusedItem + 1) : this.expandAndFocus();
  }

  @Method()
  async handlePrevious(e) {
    stopEventAndImmediatePropagation(e);
    return this.expanded ? this.setFocus(this.focusedItem - 1) : this.expandAndFocus();
  }

  @Method()
  async setFocus(index: number = undefined) {
    this.focusedItem = index;
    return this.focusFirst() && this.focusLast();
  }

  @Method()
  async setSelect(index: number) {
    this.selectedIndex = index;
    const item = this.parsedOptions[this.selectedIndex];
    const value = typeof item === 'string' ? item: item.value;
    setFormValue(this.internals, value)
    if (this.required)
      setValidityMissingValue(this.internals, value, this.validityMissingValueText);
    return this.selected.emit(item) && this.collapseComponent();
  }

  @Listen('click', { target: 'window' })
  handleClickOutside(e) {
    if (this.expanded && !this.host.contains(e.target)) {
      return { event: e, success: this.expanded && this.collapseComponent() };
    }
    return this.expanded;
  }

  @Listen('click')
  handleClick(e: PointerEvent) {
    return { event: e, success: this.expanded ? this.collapseComponent() : this.expandComponent() };
  }

  @Listen('keydown')
  handleKeyPress(e: KeyboardEvent) {
    switch (e.code) {
      case 'Enter':
      case 'NumpadEnter': {
        return { event: e, success: this.setSelect(this.focusedItem) };
      }
      case 'Space': {
        stopEventAndImmediatePropagation(e);
        return { event: e, success: this.expandComponent(e) };
      }
      case 'Escape': {
        stopEventAndImmediatePropagation(e);
        return { event: e, success: this.collapseComponent(e) };
      }
      case 'Tab':
        if (this.expanded) {
          return { event: e, success: this.collapseComponent(e) };
        }
        return false;
      case 'ArrowDown':
        return { event: e, success: this.handleNext(e) };
      case 'ArrowUp':
        return { event: e, success: this.handlePrevious(e) };
      default:
        return { event: e, success: false };
    }
  }

  @Watch('focusedItem')
  watchFocusedIndex(newIndex: number) {
    const container = this.host.shadowRoot.querySelector('.anchor-c-select__select-items-container');
    const activeElement = container.querySelector(`.select-item--${newIndex}`);
    activeElement?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });
  }

  @Watch('parsedOptions')
  watchParsedOptions() {
    const item = this.parsedOptions[this.selectedIndex];
    if (!item) {
      setFormValue(this.internals, '');
      if (this.required)
        setValidityMissingValue(this.internals, null, this.validityMissingValueText);
      return;
    }
    const value = typeof item === 'string' ? item : item.value
    setFormValue(this.internals, value);
    if (this.required)
      setValidityMissingValue(this.internals, value, this.validityMissingValueText);
  }

  @Watch('selectedIndex')
  watchSelectedIndex() {
    const item = this.parsedOptions[this.selectedIndex];
    const value = typeof item === 'string' ? item : item.value
    setFormValue(this.internals, value)
    if (this.required)
      setValidityMissingValue(this.internals, value, this.validityMissingValueText);
  }

  render() {
    const selectedValue = this.parsedOptions ? this.parsedOptions[this.selectedIndex] ?? this.initialValue : null;
    const isSelected = index => this.selectedIndex === index;
    const isFocused = index => this.focusedItem === index;
    return (
      <Host role="listbox">
        <div
          id={this.selectId}
          class={{
            'anchor-c-select': true,
            'disabled': this.disabled,
            'error': this.error,
            'expanded': this.expanded,
          }}
        >
          <anchor-label
            class={{
              'anchor-c-label': true,
            }}
            for={this.selectId}
          >
            <slot name="label" />
          </anchor-label>
          <slot name="help-text" />
          {this.error && (
            <span class="anchor-c-select__error-msg">
              <slot name="error-message" />
              <anchor-icon color="inherit" size="small" name="alert-circle"></anchor-icon>
            </span>
          )}
          <div class="anchor-c-select__select-items-container">
            <anchor-option
              tabindex={0}
              label={typeof selectedValue === 'string' ? selectedValue : selectedValue?.label}
              value={typeof selectedValue === 'string' ? selectedValue : selectedValue?.value}
              itemId={`${this.selectId}-option-placeholder`}
              class={{
                'main-element': true,
                'expanded': this.expanded,
                'disabled': this.disabled,
                'error': this.error,
              }}
              selected={selectedValue !== this.initialValue && this.expanded}
              disabled={this.disabled}
              icon={`chevron-${this.expanded ? 'up' : 'down'}`}
            />
            <div class={{ 'anchor-c-select__select-items': true, 'expanded': this.expanded }}>
              {this.expanded &&
                this.parsedOptions.map((option, index) => (
                  <anchor-option
                    class={`select-item select-item--${index}`}
                    value={option?.value ?? option}
                    label={option.label}
                    description={option.description}
                    selected={isSelected(index)}
                    icon={isSelected(index) ? 'check' : undefined}
                    focused={isFocused(index)}
                    itemId={`${this.selectId}-option-${String(index)}`}
                    onClick={e => this.handlePressed(e, index)}
                    role="option"
                  />
                ))}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
